import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Mail, X, Send, Paperclip, FileText, Search } from 'lucide-react';
import Pagination from '../../components/admin/Pagination';
import { useAuth } from '../../App';
import { getContacts, createContact, deleteContact, sendEmailToContact, getAttachmentBlob } from '../../api/contacts';
import { getTemplates } from '../../api/templates';
import AdminLayout from './AdminLayout';

// One attachment: DB-stored files are fetched with admin auth as a Blob and shown
// inline (image) or as a download link (PDF/other). Legacy items with a direct `url` just use it.
const AttachmentThumb = ({ att, token }) => {
  const isImg = (att.content_type || '').startsWith('image/');
  const [url, setUrl] = useState(att.url || null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (att.url || !att.att_id) return;           // direct url (legacy) — nothing to fetch
    let obj; let cancelled = false;
    getAttachmentBlob(token, att.att_id)
      .then(blob => { obj = URL.createObjectURL(blob); if (!cancelled) setUrl(obj); })
      .catch(() => { if (!cancelled) setErr(true); });
    return () => { cancelled = true; if (obj) URL.revokeObjectURL(obj); };
  }, [att.att_id, att.url, token]);

  if (isImg) {
    if (url) {
      return (
        <a href={url} target="_blank" rel="noreferrer" title={att.filename}
          className="block rounded-lg border border-white/10 overflow-hidden hover:border-[#10b981] transition-colors">
          <img src={url} alt={att.filename} className="w-16 h-16 object-cover" />
        </a>
      );
    }
    return (
      <div className="w-16 h-16 rounded-lg border border-white/10 bg-[#050505] flex items-center justify-center text-[#6B7280]">
        {err ? <X size={16} /> : <div className="spinner" />}
      </div>
    );
  }
  // Non-image (PDF etc.)
  return (
    <a href={url || undefined} target="_blank" rel="noreferrer" download={att.filename}
      onClick={e => { if (!url) e.preventDefault(); }} title={att.filename}
      className={`inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-[#050505] px-2.5 py-2 text-xs max-w-[180px] truncate transition-colors ${url ? 'text-[#9CA3AF] hover:border-[#10b981]' : 'text-[#6B7280]'}`}>
      <FileText size={14} /> {att.filename}{!url && !err ? ' …' : ''}
    </a>
  );
};

const AdminContacts = () => {
  const { token } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [newContact, setNewContact] = useState({ email: '', name: '', contact_type: 'newsletter' });
  const [sendModal, setSendModal] = useState(null); // contact object
  const [sendForm, setSendForm] = useState({ subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getContacts(token, { page, limit: 10, contact_type: filter, search });
      setContacts(data.contacts);
      setTotalPages(data.pages);
    } catch (err) {} finally { setLoading(false); }
  }, [token, page, filter, search]);

  useEffect(() => { if (token) fetchContacts(); }, [token, fetchContacts]);

  // Debounce the email/name search box
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleDelete = async (id) => {
    if (!window.confirm('Usunąć kontakt?')) return;
    try {
      await deleteContact(token, id);
      fetchContacts();
    } catch (err) {}
  };

  const handleAddContact = async (e) => {
    e.preventDefault();
    try {
      await createContact(token, newContact);
      setModalOpen(false);
      setNewContact({ email: '', name: '', contact_type: 'newsletter' });
      fetchContacts();
    } catch (err) { alert('Błąd dodawania'); }
  };

  const openSendModal = async (contact) => {
    setSendModal(contact);
    // Pre-fill subject with "Re:" if contact has a subject
    const reSubject = contact.subject ? `Re: ${contact.subject}` : '';
    setSendForm({ subject: reSubject, message: '' });
    setSendResult(null);
    setSelectedTemplate('');
    try {
      const data = await getTemplates(token);
      setTemplates(data.filter(t => t.is_active));
    } catch {}
  };

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
    if (!templateId) {
      setSendForm({ subject: '', message: '' });
      return;
    }
    const tpl = templates.find(t => t.id === templateId);
    if (tpl) {
      const contactName = sendModal?.name || '';
      const rv = (text) => (text || '').replace(/\{\{imie\}\}/g, contactName);
      setSendForm({
        subject: rv(tpl.subject || ''),
        message: [rv(tpl.header_text), rv(tpl.body_text)].filter(Boolean).join('\n\n')
      });
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    setSending(true);
    setSendResult(null);
    try {
      await sendEmailToContact(token, sendModal.id, sendForm.subject, sendForm.message, sendModal.message || '');
      // Refresh so the reply appears in the conversation thread immediately
      const data = await getContacts(token, { page, limit: 10, contact_type: filter, search });
      setContacts(data.contacts);
      setTotalPages(data.pages);
      const updated = data.contacts.find(c => c.id === sendModal.id);
      if (updated) setSendModal(updated);
      setSendForm({ subject: sendModal.subject ? `Re: ${sendModal.subject}` : '', message: '' });
      setSelectedTemplate('');
      setSendResult('success');
    } catch (err) {
      setSendResult('error');
    } finally {
      setSending(false);
    }
  };

  // Build the conversation thread (fallback to the single original message for old contacts)
  const threadOf = (c) => (c?.thread && c.thread.length > 0)
    ? c.thread
    : [{ from: 'client', name: c?.name, message: c?.message, attachments: c?.attachments, created_at: c?.created_at }];


  const filters = [
    { value: 'all', label: 'Wszystkie' },
    { value: 'newsletter', label: 'Subskrybenci' },
    { value: 'message', label: 'Wiadomości' },
    { value: 'email', label: 'E-mail' }
  ];

  return (
    <AdminLayout title="Kontakty">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div className="flex flex-wrap items-center gap-2">
          {filters.map(f => (
            <button key={f.value} onClick={() => { setFilter(f.value); setPage(1); }} className={`px-4 py-2 rounded-lg text-sm transition-all ${filter === f.value ? 'bg-[#10b981] text-white' : 'bg-[#0B0F0E] text-[#9CA3AF] border border-white/10 hover:border-[#10b981]'}`}>
              {f.label}
            </button>
          ))}
          <div className="relative w-full sm:w-64">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280] pointer-events-none" />
            <input type="text" value={searchInput} onChange={e => setSearchInput(e.target.value)}
              placeholder="Szukaj po e-mailu lub imieniu…" className="w-full pl-9 pr-8" data-testid="contact-search" />
            {searchInput && (
              <button onClick={() => setSearchInput('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-white" title="Wyczyść">
                <X size={16} />
              </button>
            )}
          </div>
        </div>
        <button onClick={() => setModalOpen(true)} className="btn-primary" data-testid="add-contact-btn">
          <Plus size={18} /> Dodaj ręcznie
        </button>
      </div>

      <div className="glass-card overflow-hidden">
        {loading ? <div className="flex justify-center py-12"><div className="spinner"></div></div> : (
          <>
            <div className="overflow-x-auto">
              <table className="table-custom">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Imię</th>
                    <th>Typ</th>
                    <th>Tagi</th>
                    <th>Data</th>
                    <th>Akcje</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.length > 0 ? contacts.map(c => (
                    <tr key={c.id}>
                      <td className="text-white">
                        <div className="flex items-center gap-2">
                          {(c.contact_type === 'message' || c.contact_type === 'email') && (
                            <span
                              title={c.replied ? 'Odpowiedziano' : 'Bez odpowiedzi'}
                              className={`inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 ${c.replied ? 'bg-[#10b981]' : 'bg-amber-400'}`}
                            />
                          )}
                          <span className="truncate">{c.email}</span>
                          {c.attachments?.length > 0 && (
                            <span className="inline-flex items-center gap-0.5 text-[#6B7280] flex-shrink-0" title={`${c.attachments.length} załącznik(i)`}>
                              <Paperclip size={13} />{c.attachments.length}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="text-[#9CA3AF]">{c.name || '-'}</td>
                      <td>
                        <span className={`px-2 py-1 rounded text-xs ${c.contact_type === 'newsletter' ? 'bg-[#10b981]/20 text-[#10b981]' : c.contact_type === 'email' ? 'bg-violet-500/20 text-violet-400' : 'bg-blue-500/20 text-blue-400'}`}>
                          {c.contact_type === 'newsletter' ? 'Newsletter' : c.contact_type === 'email' ? 'E-mail' : 'Kontakt'}
                        </span>
                      </td>
                      <td>
                        <div className="flex gap-1">
                          {c.tags?.map((tag, i) => (
                            <span key={i} className="px-2 py-0.5 bg-[#050505] rounded text-xs text-[#9CA3AF]">{tag}</span>
                          ))}
                        </div>
                      </td>
                      <td className="text-[#6B7280]">{new Date(c.created_at).toLocaleDateString('pl-PL')}</td>
                      <td>
                        <div className="flex gap-1">
                          <button onClick={() => openSendModal(c)} className="p-2 text-[#9CA3AF] hover:text-[#10b981]" title="Wyślij email">
                            <Mail size={18} />
                          </button>
                          <button onClick={() => handleDelete(c.id)} className="p-2 text-[#9CA3AF] hover:text-red-400" title="Usuń">
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan={6} className="text-center text-[#6B7280] py-8">Brak kontaktów</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </>
        )}
      </div>

      {/* Conversation / Send Email Modal */}
      {sendModal && (
        <div className="modal-overlay" onClick={() => setSendModal(null)}>
          <div className="modal-content max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center p-6 border-b border-white/5">
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  Rozmowa
                  {(sendModal.contact_type === 'message' || sendModal.contact_type === 'email') && (
                    <span className={`px-2 py-0.5 rounded text-[11px] ${sendModal.replied ? 'bg-[#10b981]/20 text-[#10b981]' : 'bg-amber-400/20 text-amber-400'}`}>
                      {sendModal.replied ? 'Odpowiedziano' : 'Bez odpowiedzi'}
                    </span>
                  )}
                </h2>
                <p className="text-[#6B7280] text-sm mt-1">{sendModal.name ? `${sendModal.name} · ` : ''}{sendModal.email}</p>
              </div>
              <button onClick={() => setSendModal(null)} className="text-[#6B7280] hover:text-white"><X size={24} /></button>
            </div>

            <div className="p-6 space-y-4">
              {/* Conversation thread */}
              {(() => {
                const entries = threadOf(sendModal).filter(t => (t.message && t.message.trim()) || (t.attachments && t.attachments.length));
                if (entries.length === 0) return null;
                return (
                  <div className="max-h-[42vh] overflow-y-auto space-y-3 pr-1">
                    {entries.map((t, idx) => {
                      const isAdmin = t.from === 'admin';
                      return (
                        <div key={idx} className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[85%] rounded-xl p-3 border ${isAdmin ? 'bg-[#10b981]/10 border-[#10b981]/30' : 'bg-[#0B0F0E] border-white/10'}`}>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-semibold text-white">{isAdmin ? 'Ty (MrMinds)' : (sendModal.name || 'Klient')}</span>
                              {t.created_at && <span className="text-[10px] text-[#6B7280]">{new Date(t.created_at).toLocaleString('pl-PL')}</span>}
                            </div>
                            {isAdmin && t.subject && <p className="text-[11px] text-[#6B7280] mb-1">Temat: {t.subject}</p>}
                            {t.message && <p className="text-sm text-[#e5e7eb] whitespace-pre-wrap break-words">{t.message}</p>}
                            {t.attachments?.length > 0 && (
                              <div className="flex flex-wrap gap-2 mt-2">{t.attachments.map((a, i) => <AttachmentThumb key={i} att={a} token={token} />)}</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Reply form */}
              <form onSubmit={handleSendEmail} className="space-y-4 border-t border-white/5 pt-4">
                <p className="text-xs text-[#6B7280] uppercase tracking-wide">Twoja odpowiedź</p>
                {templates.length > 0 && (
                  <div>
                    <label className="block text-sm text-[#9CA3AF] mb-2">Szablon (opcjonalnie)</label>
                    <select value={selectedTemplate} onChange={e => handleTemplateSelect(e.target.value)} className="w-full">
                      <option value="">— bez szablonu —</option>
                      {templates.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-sm text-[#9CA3AF] mb-2">Temat *</label>
                  <input type="text" value={sendForm.subject} onChange={e => setSendForm({...sendForm, subject: e.target.value})} className="w-full" required placeholder="Temat wiadomości" />
                </div>
                <div>
                  <label className="block text-sm text-[#9CA3AF] mb-2">Treść *</label>
                  <textarea value={sendForm.message} onChange={e => setSendForm({...sendForm, message: e.target.value})} className="w-full" rows={5} required placeholder="Wpisz treść odpowiedzi..." />
                </div>
                {sendResult === 'success' && (
                  <p className="text-[#10b981] text-sm flex items-center gap-1.5"><Mail size={15} /> Odpowiedź wysłana do {sendModal.email} — zapisano w rozmowie.</p>
                )}
                {sendResult === 'error' && (
                  <p className="text-red-400 text-sm">Błąd wysyłki. Sprawdź czy Resend jest skonfigurowany.</p>
                )}
                <div className="flex gap-3 pt-1">
                  <button type="button" onClick={() => setSendModal(null)} className="btn-ghost flex-1">Zamknij</button>
                  <button type="submit" disabled={sending} className="btn-primary flex-1">
                    {sending ? <div className="spinner"></div> : <><Send size={16} /> Wyślij</>}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add Contact Modal */}
      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center p-6 border-b border-white/5">
              <h2 className="text-xl font-bold text-white">Dodaj kontakt</h2>
              <button onClick={() => setModalOpen(false)} className="text-[#6B7280] hover:text-white"><X size={24} /></button>
            </div>
            <form onSubmit={handleAddContact} className="p-6 space-y-4">
              <div>
                <label className="block text-sm text-[#9CA3AF] mb-2">Email *</label>
                <input type="email" value={newContact.email} onChange={e => setNewContact({...newContact, email: e.target.value})} className="w-full" required />
              </div>
              <div>
                <label className="block text-sm text-[#9CA3AF] mb-2">Imię</label>
                <input type="text" value={newContact.name} onChange={e => setNewContact({...newContact, name: e.target.value})} className="w-full" />
              </div>
              <div>
                <label className="block text-sm text-[#9CA3AF] mb-2">Typ</label>
                <select value={newContact.contact_type} onChange={e => setNewContact({...newContact, contact_type: e.target.value})} className="w-full">
                  <option value="newsletter">Newsletter</option>
                  <option value="message">Kontakt</option>
                </select>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setModalOpen(false)} className="btn-ghost flex-1">Anuluj</button>
                <button type="submit" className="btn-primary flex-1">Dodaj</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminContacts;